// ============================================================
// Wauvea WebXR — Zone Manager
// Creates and manages all zone panels from config
// ============================================================

import * as THREE from 'three';
import { CONFIG } from '../config.js';
import { ZonePanel } from './ZonePanel.js';

export class ZoneManager {
  constructor(scene) {
    this.scene = scene;
    this.zones = [];
    this.clickableMeshes = [];
    this.hoveredZone = null;
  }

  /**
   * Build all zone panels from config
   */
  async build() {
    const texture = await this._loadPlaceholderTexture();

    CONFIG.zones.forEach((zoneConfig, index) => {
      const zone = new ZonePanel(zoneConfig, index);
      const mesh = zone.build(texture);
      this.scene.add(mesh);
      this.zones.push(zone);
      this.clickableMeshes.push(mesh);
    });
  }

  _loadPlaceholderTexture() {
    return new Promise((resolve) => {
      const loader = new THREE.TextureLoader();
      loader.load(
        '/assets/zone-placeholder.png',
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          resolve(texture);
        },
        undefined,
        () => resolve(null) // No placeholder available, use color-only
      );
    });
  }

  /**
   * Check raycaster intersection and update hover states
   * @param {THREE.Raycaster} raycaster
   * @returns {{ zone: ZonePanel, intersection: THREE.Intersection } | null}
   */
  checkHover(raycaster) {
    const intersects = raycaster.intersectObjects(this.clickableMeshes, false);

    if (intersects.length > 0) {
      const mesh = intersects[0].object;
      const zone = this.zones.find(z => z.mesh === mesh);

      if (zone && this.hoveredZone !== zone) {
        // Unhover previous
        if (this.hoveredZone) {
          this.hoveredZone.setHovered(false);
        }
        // Hover new
        this.hoveredZone = zone;
        zone.setHovered(true);
      }

      return zone ? { zone, intersection: intersects[0] } : null;
    }

    // Nothing hovered
    if (this.hoveredZone) {
      this.hoveredZone.setHovered(false);
      this.hoveredZone = null;
    }
    return null;
  }

  /**
   * Update all zone animations
   */
  update() {
    this.zones.forEach(zone => zone.update());
  }

  /**
   * Show/hide all zone panels (used by adaptive quality during fast turns)
   */
  setAllVisible(visible) {
    this.zones.forEach(zone => {
      if (zone.mesh) zone.mesh.visible = visible;
    });
  }

  /**
   * Get zone by ID
   */
  getZoneById(id) {
    return this.zones.find(z => z.config.id === id);
  }

  /**
   * Rescale all zone panels and optionally reposition at new distance
   * Called by CalibrationPanel in real-time
   */
  rescale(scale, distance) {
    this.zones.forEach(zone => {
      if (!zone.mesh) return;

      // Apply scale
      zone.mesh.scale.set(scale, scale, 1);

      // Reposition at new distance if provided (skip overhead zones)
      if (distance !== undefined && !zone.config.overhead) {
        const cfg = zone.config;
        const angleRad = THREE.MathUtils.degToRad(cfg.angle || 0);
        const x = Math.sin(angleRad) * distance;
        const z = -Math.cos(angleRad) * distance;
        zone.mesh.position.x = x;
        zone.mesh.position.z = z;
        zone.mesh.lookAt(0, zone.mesh.position.y, 0);
      }
    });
  }

  /**
   * Load a media asset onto a specific zone (called from preview postMessage)
   * @param {string} zoneId
   * @param {string} url - Media URL
   * @param {'image'|'video'|'stream'} kind
   */
  setZoneMedia(zoneId, url, kind) {
    const zone = this.getZoneById(zoneId);
    if (zone) {
      zone.setMedia(url, kind);
    }
  }

  /**
   * Clear media from a specific zone
   * @param {string} zoneId
   */
  clearZoneMedia(zoneId) {
    const zone = this.getZoneById(zoneId);
    if (zone) {
      zone.clearMedia();
    }
  }

  /**
   * Set individual zone transform (position + size)
   * @param {string} zoneId
   * @param {{ x: number, y: number, z: number, width: number, height: number }} transform
   */
  setZoneTransform(zoneId, { x, y, z, width, height, tiltX, tiltY, tiltZ }) {
    const zone = this.getZoneById(zoneId);
    if (!zone || !zone.mesh) return;

    // Update position
    zone.mesh.position.set(x, y, z);

    // Rebuild geometry if size changed
    if (width !== undefined && height !== undefined) {
      const oldGeo = zone.mesh.geometry;
      zone.mesh.geometry = new THREE.PlaneGeometry(width, height);
      oldGeo.dispose();
    }

    // Face center first (skip overhead)
    if (!zone.config.overhead) {
      zone.mesh.lookAt(0, y, 0);
    }

    // Apply tilt rotation on top of the lookAt orientation
    if (tiltX || tiltY || tiltZ) {
      const tilt = new THREE.Euler(
        THREE.MathUtils.degToRad(tiltX || 0),
        THREE.MathUtils.degToRad(tiltY || 0),
        THREE.MathUtils.degToRad(tiltZ || 0),
        'XYZ'
      );
      const tiltQuat = new THREE.Quaternion().setFromEuler(tilt);
      zone.mesh.quaternion.multiply(tiltQuat);
    }
  }

  /**
   * Toggle edit mode — labels visible in edit, hidden in preview
   */
  setEditMode(editMode) {
    this.zones.forEach(zone => {
      if (!zone.mesh) return;
      // Labels are child meshes — toggle visibility
      zone.mesh.children.forEach(child => {
        child.visible = editMode;
      });
      // Double-sided in edit so you can see panels from behind
      zone.mesh.material.side = editMode ? THREE.DoubleSide : THREE.FrontSide;
      zone.mesh.material.needsUpdate = true;
    });
  }

  /**
   * Set the base colour of a zone panel (when no media is assigned)
   * @param {string} zoneId
   * @param {string} color — hex string
   */
  setZoneColor(zoneId, color) {
    const zone = this.getZoneById(zoneId);
    if (!zone || !zone.mesh) {
      console.warn('[ZoneManager] setZoneColor: zone not found:', zoneId);
      return;
    }
    console.log('[ZoneManager] setZoneColor:', zoneId, '→', color);
    const c = new THREE.Color(color);
    // Clear placeholder texture so solid color shows
    if (zone.mesh.material.map) {
      zone.mesh.material.map.dispose();
      zone.mesh.material.map = null;
    }
    zone.mesh.material.color.set(c);
    zone.mesh.material.emissive.set(c);
    zone.mesh.material.emissiveIntensity = 0.3;
    zone.mesh.material.needsUpdate = true;
  }

  /**
   * Set corner radius on zone panel(s) via alpha-map rounding.
   * @param {string} zoneId — zone ID, or 'all' for every zone
   * @param {number} radius — 0 = sharp, 0.1–5 = graduated rounding
   */
  setZoneCornerRadius(zoneId, radius) {
    if (zoneId === 'all') {
      this.zones.forEach(zone => zone.setCornerRadius(radius));
      console.log('[ZoneManager] setZoneCornerRadius: all →', radius);
    } else {
      const zone = this.getZoneById(zoneId);
      if (zone) {
        zone.setCornerRadius(radius);
        console.log('[ZoneManager] setZoneCornerRadius:', zoneId, '→', radius);
      } else {
        console.warn('[ZoneManager] setZoneCornerRadius: zone not found:', zoneId);
      }
    }
  }
}
